import { FC } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, FormProvider, FieldPath } from 'react-hook-form';
import useReactRouter from 'use-react-router';

import Button from 'jbc-front/components/Button';
import { ButtonRow } from 'jbc-front/components/presenters/layout/ButtonRow';

import { useEmployeeMutation as useMutation } from '@jbc-year-end-adj/2024/hooks/graphql/useMutation';
import { eachValidationErrorsForHookFrom, ServerValidationError } from '@jbc-year-end-adj/2024/hooks/graphql/error/ServerValidationError';

import { FormSection } from '@jbc-year-end-adj/2024/components/form/FormSection';
import { FileField } from '@jbc-year-end-adj/2024/components/react-hook-form/FileField';
import { FilePreview } from '@jbc-year-end-adj/2024/features/FilePreview';
import { AmountField } from '@jbc-year-end-adj/2024/components/react-hook-form/AmountField';
import { Radio } from '@jbc-year-end-adj/2024/components/form/Radio';
import { DateField } from '@jbc-year-end-adj/2024/components/react-hook-form/DateField';
import { SelectField } from '@jbc-year-end-adj/2024/components/react-hook-form/SelectField';

import { schema, Schema, generateDefaultValues } from './schema';
import { ANSWER_HOUSING_LOAN, convertFormDataToQueryVariables } from './mutation';
import { Request } from './query';
import { specialDeductionOptiosn, deductionClassificationsOptions } from './consts';

type FormProps = {
  request: Request;
  imageSetting: 'none' | 'optional' | 'required';
};

export const Form: FC<FormProps> = ({ request, imageSetting }) => {
  const [update, { loading }] = useMutation(ANSWER_HOUSING_LOAN);
  const { history } = useReactRouter();

  const imageRequired = imageSetting === 'required';
  const methods = useForm<Schema>({
    defaultValues: generateDefaultValues(request.profile.housingLoan),
    resolver: yupResolver(schema(imageRequired)),
    mode: 'onSubmit'
  });

  const {
    handleSubmit,
    register,
    formState: { errors },
    setValue,
    setError,
    watch
  } = methods;

  const onSubmit = async (data: Schema) => {
    const variables = convertFormDataToQueryVariables(data);
    try {
      await update({ variables });
      history.push('/employee_input');
    } catch (err) {
      if (err instanceof ServerValidationError) {
        eachValidationErrorsForHookFrom(err.validationErrors, schema(imageRequired), data, (path, error) => {
          setError(path as FieldPath<Schema>, error);
        });
        return;
      }
      throw err;
    }
  };

  const housingLoanDeductionApplicableCount = watch('housingLoanDeductionApplicableCount');
  const image = watch('image');
  const certificateImage = watch('certificateImage');

  return (
    <FormProvider {...methods}>
      <form>
        <FormSection>
          <Radio.Container
            label="住宅借入金等 特別控除適用数"
            required
            note="適用数3以上の場合管理者にお問い合わせください。"
            error={errors.housingLoanDeductionApplicableCount && errors.housingLoanDeductionApplicableCount?.message}
          >
            <Radio {...register(`housingLoanDeductionApplicableCount`)} value="1">
              1
            </Radio>
            <Radio {...register(`housingLoanDeductionApplicableCount`)} value="2">
              2
            </Radio>
          </Radio.Container>

          <AmountField
            label="住宅借入金等特別控除額"
            required
            {...register(`housingLoanDeductionApplicableAmount`)}
            error={errors.housingLoanDeductionApplicableAmount && errors.housingLoanDeductionApplicableAmount?.message}
            isError={errors.housingLoanDeductionApplicableAmount && !!errors.housingLoanDeductionApplicableAmount?.message}
          />

          <DateField
            label="居住開始年月日（1回目）"
            required
            note="住宅借入金等特別控除申告書の㋑または㋠を転記してください。"
            name={'firstDetail.residenceStartOn'}
            error={errors.firstDetail && errors.firstDetail.residenceStartOn?.message}
          />

          <SelectField
            label="住宅借入金等特別控除区分（1回目）"
            required
            options={deductionClassificationsOptions}
            name="firstDetail.deductionClassification"
          />

          <SelectField
            label="住宅借入金等特別控除区分（1回目）　特定"
            required
            options={specialDeductionOptiosn}
            name="firstDetail.specialDeduction"
            note="住宅借入金等特別控除申告書の㋑または㋠に（特定）等の表記がある場合に選択してください。"
          />

          <AmountField
            label="住宅借入金等 年末残高(1回目)"
            {...register(`firstDetail.yearEndBalance`)}
            required
            error={errors.firstDetail && errors.firstDetail.yearEndBalance?.message}
            isError={errors.firstDetail && !!errors.firstDetail.yearEndBalance?.message}
          />

          {housingLoanDeductionApplicableCount === '2' && (
            <>
              <DateField
                label="居住開始年月日（2回目）"
                required
                note="住宅借入金等特別控除申告書の㋠を転記してください。"
                name={'secondDetail.residenceStartOn'}
                error={errors.secondDetail && errors.secondDetail.residenceStartOn?.message}
              />

              <SelectField
                label="住宅借入金等特別控除区分（2回目）"
                required
                options={deductionClassificationsOptions}
                name="secondDetail.deductionClassification"
              />

              <SelectField
                label="住宅借入金等特別控除区分（2回目）　特定"
                required
                options={specialDeductionOptiosn}
                name="secondDetail.specialDeduction"
                note="住宅借入金等特別控除申告書の㋠に（特定）等の表記がある場合に選択してください。"
              />

              <AmountField
                label="住宅借入金等 年末残高(2回目)"
                {...register(`secondDetail.yearEndBalance`)}
                required
                error={errors.secondDetail && errors.secondDetail.yearEndBalance?.message}
                isError={errors.secondDetail && !!errors.secondDetail.yearEndBalance?.message}
              />
            </>
          )}

          {imageSetting !== 'none' && (
            <>
              <FileField
                label="住宅借入金等特別控除申告書の画像"
                note="対応可能形式：jpg, jpeg, gif, png, pdf"
                name={`image`}
                required={imageSetting === 'required'}
                error={errors.image && errors.image.message}
              >
                {image && (
                  <FileField.FileContainer>
                    <FileField.File file={image} onRemove={() => setValue(`image`, undefined)} />
                    <FilePreview file={image} />
                  </FileField.FileContainer>
                )}
              </FileField>

              <FileField
                label="住宅ローンの年末残高証明書の画像"
                note="対応可能形式：jpg, jpeg, gif, png, pdf"
                name={`certificateImage`}
                required={imageSetting === 'required'}
                error={errors.certificateImage && errors.certificateImage.message}
              >
                {certificateImage && (
                  <FileField.FileContainer>
                    <FileField.File file={certificateImage} onRemove={() => setValue(`certificateImage`, undefined)} />
                    <FilePreview file={certificateImage} />
                  </FileField.FileContainer>
                )}
              </FileField>
            </>
          )}

          <ButtonRow>
            <Button primary onClick={handleSubmit(onSubmit)} disabled={loading}>
              次へ
            </Button>
          </ButtonRow>
        </FormSection>
      </form>
    </FormProvider>
  );
};
